/*
 * style-preview-player.js — shared sticky-less WAVEFORM preview player for the
 * AI music tools' style-sample previews (song-generator + music-generator).
 *
 * Self-hosted, ZERO external deps: the waveform is drawn on a <canvas> from
 * real peaks computed via Web Audio decodeAudioData. Playback itself uses a
 * plain <audio> element (instant streaming + seek + volume); Web Audio is used
 * ONLY to compute the peak envelope. Decoded peaks are cached per URL so
 * switching back to a style is instant.
 *
 * ES2019-SAFE (raw-served via asset('js/…'), scanned by
 * scripts/check-es2019-raw.mjs): no optional chaining (?.), no nullish (??),
 * no logical-assignment, no numeric separators, no private class fields, no
 * BigInt. Conservative var + function style to match the sibling tool scripts.
 *
 * Public surface:
 *   window.StylePreviewPlayer.mount(opts) -> controller
 *     opts = {
 *       mountEl:      element the player bar is appended into (top of workspace)
 *       sampleBase:   '/audio/ai-styles'  (no trailing slash)
 *       ae:           tool-scoped telemetry fn  ae(action, extra)  (optional)
 *       labels:       { play, pause, volume, volLevel, mute, unmute, seek, now }
 *                     (volLevel is optional — a slider aria-valuetext template
 *                      with a ':pct' placeholder, e.g. "Volume :pct%")
 *       onFirstReveal:fn() fired once, the first time the bar reveals (optional)
 *       onState:      fn(activeKey, isPlaying) — blade repaints its card ▶ indicators
 *       onMissing:    fn(key) — blade shows its own «пример скоро» toast + resets the card
 *       settingsTool: __toolSettings namespace for the persisted volume (default 'ai-preview')
 *       locale:       BCP-47 string (unused today, reserved)
 *       rtl:          boolean — mirror layout + waveform direction
 *     }
 *   controller = { play(key,title), toggle(key,title), hide(), isPlaying(key), destroy() }
 *
 * The bar is a NORMAL in-flow element at the top of the workspace — it is NOT
 * position:sticky and does NOT follow the scroll (deliberate, per product).
 */
(function () {
  'use strict';

  if (window.StylePreviewPlayer && window.StylePreviewPlayer.__real) { return; }

  var BUCKETS = 800;            // stored peak resolution (envelope is res-independent on draw)
  var REVEAL_MS = 360;          // reveal/collapse transition duration
  var SEEK_AE_MS = 500;         // sample_seek telemetry throttle

  /* ── shared, cross-instance decode state ───────────────────────────────── */
  var peaksCache = {};          // url -> { min:Float32Array, max:Float32Array, n }
  var peaksPending = {};        // url -> true while a decode is in flight
  var _actx = null;

  function audioCtx() {
    if (_actx) { return _actx; }
    try {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (AC) { _actx = new AC(); }
    } catch (e) { _actx = null; }
    return _actx;
  }

  /* ── one-time CSS injection (style-src allows 'unsafe-inline') ──────────── */
  var CSS_ID = 'spp-styles';
  function injectCss() {
    if (document.getElementById(CSS_ID)) { return; }
    var css = ''
      + '.spp-host{overflow:hidden;max-height:0;opacity:0;'
      +   'transition:max-height ' + (REVEAL_MS / 1000) + 's ease,opacity ' + (REVEAL_MS / 1000) + 's ease;'
      +   'position:relative;z-index:5}'
      + '@media(prefers-reduced-motion:reduce){.spp-host{transition:none}}'
      + '.spp-bar{display:flex;flex-wrap:wrap;align-items:center;gap:10px 12px;margin:0 0 16px;'
      +   'padding:10px 12px;border-radius:14px;border:1px solid var(--lg-border,var(--border,#2a2f3a));'
      +   'background:var(--lg-surface,var(--glass,rgba(255,255,255,.04)));box-shadow:0 6px 22px rgba(0,0,0,.18)}'
      + '.spp-btn{flex:0 0 auto;width:44px;height:44px;min-width:44px;border-radius:50%;'
      +   'border:1px solid var(--lg-border-hi,var(--border-hover,#3a4150));'
      +   'background:var(--lg-surface-2,var(--glass,rgba(255,255,255,.06)));'
      +   'color:var(--lg-text,var(--text-primary,#e8eaee));display:inline-flex;align-items:center;'
      +   'justify-content:center;cursor:pointer;padding:0;'
      +   'transition:background .15s,border-color .15s,color .15s,filter .15s}'
      + '.spp-btn:hover{border-color:var(--lg-accent,#7aa7ff);color:var(--lg-accent,#7aa7ff)}'
      + '.spp-btn:focus-visible{outline:2px solid var(--lg-accent,#7aa7ff);outline-offset:2px}'
      + '.spp-btn svg{width:18px;height:18px;display:block;pointer-events:none}'
      + '.spp-play{background:linear-gradient(135deg,var(--lg-accent,#7aa7ff),var(--lg-accent-2,#b78bff));'
      +   'color:#fff;border-color:transparent}'
      + '.spp-play:hover{color:#fff;filter:brightness(1.07)}'
      + '.spp-main{flex:1 1 220px;min-width:0;display:flex;flex-direction:column;gap:4px}'
      + '.spp-meta{display:flex;align-items:baseline;gap:10px;min-width:0}'
      + '.spp-label{font-size:12px;font-weight:600;color:var(--lg-text-3,var(--text-muted,#9aa1ad));'
      +   'flex:0 0 auto}'
      + '.spp-title{font-size:13px;font-weight:700;color:var(--lg-text,var(--text-primary,#e8eaee));'
      +   'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:1;min-width:0}'
      + '.spp-time{font-size:12px;color:var(--lg-text-3,var(--text-muted,#9aa1ad));'
      +   'font-variant-numeric:tabular-nums;flex:0 0 auto}'
      + '.spp-wave{display:block;width:100%;height:40px;cursor:pointer;border-radius:8px;'
      +   'touch-action:none;background:transparent}'
      + '.spp-wave:focus-visible{outline:2px solid var(--lg-accent,#7aa7ff);outline-offset:2px}'
      + '.spp-vol{position:relative;flex:0 0 auto;display:flex;align-items:center}'
      // YouTube-style vertical volume popup — absolutely positioned ABOVE the
      // button (longhands only, never `inset:`; centered so it is inherently
      // RTL-safe). Hidden via visibility so the rotated slider is out of the tab
      // order while collapsed.
      + '.spp-vol-pop{position:absolute;left:50%;bottom:100%;top:auto;right:auto;'
      +   'margin-bottom:6px;transform:translateX(-50%) translateY(4px);'
      +   'display:flex;flex-direction:column;align-items:center;padding:10px 8px;'
      +   'border-radius:12px;border:1px solid var(--lg-border,var(--border,#2a2f3a));'
      +   'background:var(--lg-surface,var(--glass,rgba(22,24,30,.97)));'
      +   'box-shadow:0 8px 24px rgba(0,0,0,.34);z-index:20;'
      +   'opacity:0;visibility:hidden;pointer-events:none;'
      +   'transition:opacity .15s ease,transform .15s ease,visibility 0s linear .15s}'
      + '.spp-vol-pop.is-open{opacity:1;visibility:visible;pointer-events:auto;'
      +   'transform:translateX(-50%) translateY(0);'
      +   'transition:opacity .15s ease,transform .15s ease,visibility 0s}'
      + '@media(prefers-reduced-motion:reduce){.spp-vol-pop,.spp-vol-pop.is-open{transition:none}}'
      // Rotated wrapper with an explicit box → the range is absolutely centered
      // inside and rotated -90deg (top = louder, bottom = quieter). This is the
      // most robust cross-browser vertical-range approach (no writing-mode gaps).
      + '.spp-vol-rotate{position:relative;width:34px;height:104px}'
      // Selector is scoped under .spp-vol-rotate (specificity 0,2,0) so the
      // 100px length beats the app-wide `.lg-app input{width:100%}` reset that
      // would otherwise shrink the range to the 34px wrapper.
      + '.spp-vol-rotate .spp-vol-range{position:absolute;top:50%;left:50%;width:100px;height:20px;'
      +   'margin:0;cursor:pointer;accent-color:var(--lg-accent,#7aa7ff);'
      +   'transform:translate(-50%,-50%) rotate(-90deg);transform-origin:center center}'
      + '.spp-vol-rotate .spp-vol-range:focus-visible{outline:2px solid var(--lg-accent,#7aa7ff);outline-offset:2px}'
      + '@media(max-width:600px){.spp-title{white-space:normal}}'
      + '[dir=rtl] .spp-play svg,[dir=rtl] .spp-mute svg{transform:scaleX(-1)}';
    var el = document.createElement('style');
    el.id = CSS_ID;
    el.textContent = css;
    (document.head || document.documentElement).appendChild(el);
  }

  var ICON_PLAY = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
  var ICON_PAUSE = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>';
  // Speaker glyphs reflecting the level: high (two arcs), low (one arc), muted (×).
  var ICON_VOL = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 10v4h4l5 4V6L7 10H3z"/><path d="M15.5 9a3.5 3.5 0 010 6M18.5 6.5a7 7 0 010 11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  var ICON_VOL_LOW = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 10v4h4l5 4V6L7 10H3z"/><path d="M15.5 9a3.5 3.5 0 010 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  var ICON_MUTE = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 10v4h4l5 4V6L7 10H3z"/><path d="M16 9.5l5 5M21 9.5l-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';

  function mmss(sec) {
    if (!isFinite(sec) || sec < 0) { sec = 0; }
    sec = Math.round(sec);
    var m = Math.floor(sec / 60);
    var s = sec % 60;
    return m + ':' + (s < 10 ? '0' + s : '' + s);
  }

  function cssVar(name, fallback) {
    try {
      var v = getComputedStyle(document.documentElement).getPropertyValue(name);
      v = (v || '').trim();
      return v ? v : fallback;
    } catch (e) { return fallback; }
  }

  /* ── peak envelope from a decoded AudioBuffer ──────────────────────────── */
  function peaksFromBuffer(ab, buckets) {
    var len = 0;
    var ch0 = null, ch1 = null;
    try {
      ch0 = ab.numberOfChannels > 0 ? ab.getChannelData(0) : null;
      ch1 = ab.numberOfChannels > 1 ? ab.getChannelData(1) : null;
      len = ch0 ? ch0.length : 0;
    } catch (e) { return null; }
    if (!len) { return null; }
    var mins = new Float32Array(buckets);
    var maxs = new Float32Array(buckets);
    var step = len / buckets;
    var peak = 0;
    for (var b = 0; b < buckets; b++) {
      var start = Math.floor(b * step);
      var end = Math.floor((b + 1) * step);
      if (end <= start) { end = start + 1; }
      if (end > len) { end = len; }
      var mn = 0, mx = 0, seen = false;
      for (var i = start; i < end; i++) {
        var v = ch0[i];
        if (ch1) { v = (v + ch1[i]) * 0.5; }
        if (!seen) { mn = v; mx = v; seen = true; }
        else {
          if (v < mn) { mn = v; }
          if (v > mx) { mx = v; }
        }
      }
      mins[b] = mn; maxs[b] = mx;
      var am = mn < 0 ? -mn : mn;
      var ax = mx < 0 ? -mx : mx;
      if (am > peak) { peak = am; }
      if (ax > peak) { peak = ax; }
    }
    // Lift quiet previews so the envelope is legible (capped gain, no clipping).
    if (peak > 0 && peak < 0.92) {
      var g = 0.92 / peak;
      if (g > 3) { g = 3; }
      for (var k = 0; k < buckets; k++) { mins[k] *= g; maxs[k] *= g; }
    }
    return { min: mins, max: maxs, n: buckets };
  }

  function fetchDecode(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) { throw new Error('http ' + r.status); }
        return r.arrayBuffer();
      })
      .then(function (buf) {
        var ctxA = audioCtx();
        if (!ctxA) { throw new Error('no-audioctx'); }
        return new Promise(function (resolve, reject) {
          var ret;
          try {
            ret = ctxA.decodeAudioData(buf, function (ab) { resolve(ab); }, function (e) { reject(e || new Error('decode')); });
          } catch (e) { reject(e); return; }
          if (ret && typeof ret.then === 'function') { ret.then(resolve, reject); }
        });
      })
      .then(function (audioBuffer) { return peaksFromBuffer(audioBuffer, BUCKETS); });
  }

  /* ══════════════════════════════════════════════════════════════════════ */
  function mount(opts) {
    opts = opts || {};
    injectCss();

    var labels = opts.labels || {};
    function lbl(k, d) { return (labels && typeof labels[k] === 'string' && labels[k]) ? labels[k] : d; }

    var sampleBase = (opts.sampleBase || '/audio/ai-styles').replace(/\/+$/, '');
    // Cache-bust for regenerated samples: the .mp3s carry a 1-year immutable
    // cache header, so a replaced sample stays stale under the same URL (server
    // + browser). The host passes a version stamp (max sample mtime) → we append
    // '?v=<ver>' so a changed sample is a fresh URL. Optional / backward-compat.
    var sampleVer = opts.sampleVer ? String(opts.sampleVer) : '';
    var aeFn = typeof opts.ae === 'function' ? opts.ae : null;
    var onStateCb = typeof opts.onState === 'function' ? opts.onState : null;
    var onMissingCb = typeof opts.onMissing === 'function' ? opts.onMissing : null;
    var onFirstReveal = typeof opts.onFirstReveal === 'function' ? opts.onFirstReveal : null;
    var settingsTool = opts.settingsTool || 'ai-preview';
    var rtl = !!opts.rtl;
    if (!rtl) {
      try { rtl = (document.documentElement.getAttribute('dir') === 'rtl'); } catch (e) { rtl = false; }
    }

    var mountEl = opts.mountEl;
    if (!mountEl) { return stubController(); }

    /* ── build DOM ── */
    var host = document.createElement('div');
    host.className = 'spp-host';

    var bar = document.createElement('div');
    bar.className = 'spp-bar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', lbl('now', 'Style preview'));

    var playBtn = document.createElement('button');
    playBtn.type = 'button';
    playBtn.className = 'spp-btn spp-play';
    playBtn.innerHTML = ICON_PLAY;
    playBtn.setAttribute('aria-label', lbl('play', 'Play'));

    var main = document.createElement('div');
    main.className = 'spp-main';

    var meta = document.createElement('div');
    meta.className = 'spp-meta';
    var labelEl = document.createElement('span');
    labelEl.className = 'spp-label';
    labelEl.textContent = lbl('now', 'Style preview');
    var titleEl = document.createElement('span');
    titleEl.className = 'spp-title';
    titleEl.textContent = '';
    var timeEl = document.createElement('span');
    timeEl.className = 'spp-time';
    var curEl = document.createElement('span');
    curEl.textContent = '0:00';
    var durEl = document.createElement('span');
    durEl.textContent = '0:00';
    timeEl.appendChild(curEl);
    timeEl.appendChild(document.createTextNode(' / '));
    timeEl.appendChild(durEl);
    meta.appendChild(labelEl);
    meta.appendChild(titleEl);
    meta.appendChild(timeEl);

    var canvas = document.createElement('canvas');
    canvas.className = 'spp-wave';
    canvas.setAttribute('tabindex', '0');
    canvas.setAttribute('role', 'slider');
    canvas.setAttribute('aria-label', lbl('seek', 'Seek'));
    canvas.setAttribute('aria-valuemin', '0');
    canvas.setAttribute('aria-valuemax', '100');
    canvas.setAttribute('aria-valuenow', '0');

    main.appendChild(meta);
    main.appendChild(canvas);

    // Volume group: a speaker/mute button + a vertical-slider popup that opens
    // ABOVE it (YouTube pattern). The button itself is the mute toggle on hover
    // devices; on touch it toggles the popup (there is no hover to reveal it).
    var vol = document.createElement('div');
    vol.className = 'spp-vol';
    var muteBtn = document.createElement('button');
    muteBtn.type = 'button';
    muteBtn.className = 'spp-btn spp-mute';
    muteBtn.innerHTML = ICON_VOL;
    muteBtn.setAttribute('aria-label', lbl('mute', 'Mute'));
    muteBtn.setAttribute('aria-haspopup', 'true');
    muteBtn.setAttribute('aria-expanded', 'false');

    var volPop = document.createElement('div');
    volPop.className = 'spp-vol-pop';
    var volRotate = document.createElement('div');
    volRotate.className = 'spp-vol-rotate';
    var range = document.createElement('input');
    range.type = 'range';
    range.className = 'spp-vol-range';
    range.min = '0';
    range.max = '100';
    range.step = '1';
    range.setAttribute('aria-label', lbl('volume', 'Volume'));
    range.setAttribute('aria-orientation', 'vertical');
    volRotate.appendChild(range);
    volPop.appendChild(volRotate);
    vol.appendChild(muteBtn);
    vol.appendChild(volPop);

    bar.appendChild(playBtn);
    bar.appendChild(main);
    bar.appendChild(vol);
    host.appendChild(bar);
    mountEl.appendChild(host);

    var ctx = null;
    try { ctx = canvas.getContext('2d'); } catch (e) { ctx = null; }

    var accentCol = cssVar('--lg-accent', '#7aa7ff');
    var baseCol = 'rgba(140,146,158,.42)';

    /* ── audio engine (streaming playback) ── */
    var audio = new Audio();
    audio.preload = 'none';
    audio.setAttribute('playsinline', '');

    /* ── state ── */
    var committed = null;   // { key, title, url } — last successfully-playing track shown
    var pending = null;     // { key, title, url } — track currently trying to load
    var activeKey = null;
    var activeUrl = null;
    var playing = false;
    var revealed = false;
    var firstRevealFired = false;
    var duration = 0;
    var curTime = 0;
    var dragging = false;
    var peaksReady = false;
    var rafId = 0;
    var phase = 0;
    var lastSeekAe = 0;

    /* ── volume (persisted, shared across both tools via settingsTool) ── */
    var vLevel = 1, vMuted = false;
    try {
      if (window.__toolSettings) {
        var sv = window.__toolSettings.load(settingsTool, 'vol', null);
        var sm = window.__toolSettings.load(settingsTool, 'muted', null);
        if (typeof sv === 'number' && sv >= 0 && sv <= 1) { vLevel = sv; }
        if (sm === true || sm === false) { vMuted = sm; }
      }
    } catch (e) {}
    function saveVol() {
      try {
        if (window.__toolSettings) {
          window.__toolSettings.save(settingsTool, 'vol', vLevel);
          window.__toolSettings.save(settingsTool, 'muted', vMuted);
        }
      } catch (e) {}
    }
    function volIcon() {
      if (vMuted || vLevel <= 0) { return ICON_MUTE; }
      if (vLevel <= 0.5) { return ICON_VOL_LOW; }
      return ICON_VOL;
    }
    function volValueText() {
      var pct = Math.round(vLevel * 100);
      var tmpl = lbl('volLevel', '');
      if (tmpl) { return tmpl.split(':pct').join(String(pct)); }
      return lbl('volume', 'Volume') + ' ' + pct + '%';
    }
    function applyVol() {
      audio.volume = vLevel;
      audio.muted = vMuted;
      range.value = String(Math.round(vLevel * 100));
      range.setAttribute('aria-valuetext', volValueText());
      muteBtn.innerHTML = volIcon();
      muteBtn.setAttribute('aria-label', vMuted ? lbl('unmute', 'Unmute') : lbl('mute', 'Mute'));
    }
    applyVol();

    /* ── volume popup open/close (hover on desktop, tap on touch) ── */
    var canHover = true;
    try { canHover = !window.matchMedia || window.matchMedia('(hover: hover)').matches; } catch (e) { canHover = true; }
    var volOpen = false, volHideTimer = 0;
    function clearVolHide() {
      if (volHideTimer) { window.clearTimeout(volHideTimer); volHideTimer = 0; }
    }
    function openVol() {
      clearVolHide();
      if (volOpen) { return; }
      volOpen = true;
      volPop.classList.add('is-open');
      muteBtn.setAttribute('aria-expanded', 'true');
    }
    function closeVol() {
      clearVolHide();
      if (!volOpen) { return; }
      volOpen = false;
      volPop.classList.remove('is-open');
      muteBtn.setAttribute('aria-expanded', 'false');
    }
    function scheduleVolHide() {
      clearVolHide();
      volHideTimer = window.setTimeout(closeVol, 260);
    }
    // The popup sits OUTSIDE the button's box, so "still inside the group" must
    // union the button + popup rects (used to decide a re-hide after a drag).
    function pointerInVolGroup(x, y) {
      function hit(el) {
        var r = el.getBoundingClientRect();
        return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      }
      return hit(vol) || hit(volPop);
    }

    range.addEventListener('input', function () {
      var v = Number(range.value) / 100;
      if (!isFinite(v)) { v = 0; }
      vLevel = v;
      if (vLevel > 0 && vMuted) { vMuted = false; }
      applyVol();
      saveVol();
      clearVolHide();
    });
    function toggleMute() {
      vMuted = !vMuted;
      if (!vMuted && vLevel === 0) { vLevel = 0.5; }
      applyVol();
      saveVol();
    }
    muteBtn.addEventListener('click', function () {
      if (canHover) {
        toggleMute();                                    // desktop: hover reveals the popup
      } else {
        if (volOpen) { closeVol(); } else { openVol(); } // touch: tap toggles the popup
      }
    });

    if (canHover) {
      vol.addEventListener('mouseenter', openVol);
      vol.addEventListener('mouseleave', scheduleVolHide);
      vol.addEventListener('focusin', openVol);          // keyboard: reveal so Tab reaches the slider
    }
    vol.addEventListener('focusout', function (e) {
      var to = e.relatedTarget;
      if (to && vol.contains(to)) { return; }            // focus stayed within the group
      scheduleVolHide();
    });
    // Keep it open across a thumb drag; a range grabs pointer capture so the
    // container's mouseleave can be swallowed — re-hide only when the drag
    // released outside the button+popup group.
    range.addEventListener('pointerdown', clearVolHide);
    range.addEventListener('pointerup', function (e) {
      if (canHover && !pointerInVolGroup(e.clientX, e.clientY)) { scheduleVolHide(); }
    });
    // outside tap/click dismisses (touch, and stray desktop clicks)
    document.addEventListener('click', function (e) {
      if (!volOpen) { return; }
      if (vol.contains(e.target)) { return; }
      closeVol();
    });

    /* ── canvas sizing + drawing ── */
    var cssW = 1, cssH = 40, dpr = 1;
    function fit() {
      var rect = canvas.getBoundingClientRect();
      cssW = Math.max(1, Math.round(rect.width));
      cssH = Math.max(1, Math.round(rect.height));
      var d = window.devicePixelRatio || 1;
      if (d > 2) { d = 2; }
      dpr = d;
      var nw = Math.round(cssW * dpr), nh = Math.round(cssH * dpr);
      if (canvas.width !== nw) { canvas.width = nw; }
      if (canvas.height !== nh) { canvas.height = nh; }
    }

    function envelopePath(pk, W, mid) {
      var n = pk.n, g = 0.9, i, x, y;
      ctx.beginPath();
      for (i = 0; i < n; i++) {
        x = n === 1 ? 0 : (i / (n - 1)) * W;
        y = mid - pk.max[i] * mid * g;
        if (i === 0) { ctx.moveTo(x, y); } else { ctx.lineTo(x, y); }
      }
      for (i = n - 1; i >= 0; i--) {
        x = n === 1 ? 0 : (i / (n - 1)) * W;
        y = mid - pk.min[i] * mid * g;
        ctx.lineTo(x, y);
      }
      ctx.closePath();
    }

    function drawPlaceholder(W, H, mid) {
      var bars = 46, bw = W / bars, i, x, amp, h, d, hl;
      var sweep = (phase * 0.012) % 1;
      for (i = 0; i < bars; i++) {
        x = i * bw;
        amp = 0.14 + 0.10 * Math.sin(i * 0.7 + phase * 0.05);
        h = Math.max(2, amp * H);
        d = Math.abs((i / bars) - sweep);
        hl = d < 0.07 ? 0.42 : 0;
        ctx.fillStyle = 'rgba(140,146,158,' + (0.22 + hl).toFixed(3) + ')';
        ctx.fillRect(x, mid - h / 2, Math.max(1, bw * 0.58), h);
      }
    }

    function draw() {
      if (!ctx) { return; }
      fit();
      var W = cssW, H = cssH, mid = H / 2;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      if (rtl) { ctx.translate(W, 0); ctx.scale(-1, 1); }
      var pk = activeUrl ? peaksCache[activeUrl] : null;
      if (pk && pk.n) {
        var ratio = duration > 0 ? (curTime / duration) : 0;
        if (ratio < 0) { ratio = 0; }
        if (ratio > 1) { ratio = 1; }
        var progX = ratio * W;
        // unplayed (full)
        ctx.save();
        ctx.beginPath(); ctx.rect(0, 0, W, H); ctx.clip();
        envelopePath(pk, W, mid);
        ctx.fillStyle = baseCol;
        ctx.fill();
        ctx.restore();
        // played (clipped to progress; in RTL space this fills from the right)
        if (progX > 0.5) {
          ctx.save();
          ctx.beginPath(); ctx.rect(0, 0, progX, H); ctx.clip();
          envelopePath(pk, W, mid);
          ctx.fillStyle = accentCol;
          ctx.fill();
          ctx.restore();
        }
      } else {
        drawPlaceholder(W, H, mid);
      }
    }

    function tickPlaceholder() {
      if (!(revealed && !peaksReady)) { rafId = 0; return; }
      phase += 1;
      draw();
      rafId = window.requestAnimationFrame(tickPlaceholder);
    }
    function startPlaceholderAnim() {
      if (!rafId && window.requestAnimationFrame) { rafId = window.requestAnimationFrame(tickPlaceholder); }
    }
    function stopPlaceholderAnim() {
      if (rafId && window.cancelAnimationFrame) { window.cancelAnimationFrame(rafId); }
      rafId = 0;
    }

    /* ── peaks orchestration ── */
    function ensurePeaks(url) {
      if (!url) { return; }
      if (peaksCache[url]) {
        if (url === activeUrl) { peaksReady = true; stopPlaceholderAnim(); draw(); }
        return;
      }
      if (url === activeUrl) { peaksReady = false; startPlaceholderAnim(); }
      if (peaksPending[url]) { return; }
      peaksPending[url] = true;
      fetchDecode(url).then(function (pk) {
        peaksPending[url] = false;
        if (pk) { peaksCache[url] = pk; }
        if (url === activeUrl) {
          peaksReady = !!peaksCache[url];
          if (peaksReady) { stopPlaceholderAnim(); }
          draw();
        }
      }).catch(function () {
        peaksPending[url] = false;
        // leave the placeholder; a later switch back can retry the decode
      });
    }

    /* ── time / aria readouts ── */
    function updateTime() {
      curEl.textContent = mmss(curTime);
      durEl.textContent = mmss(duration);
      var pct = duration > 0 ? Math.round((curTime / duration) * 100) : 0;
      canvas.setAttribute('aria-valuenow', String(pct));
      canvas.setAttribute('aria-valuetext', mmss(curTime) + ' / ' + mmss(duration));
    }

    function setTitle(title) {
      titleEl.textContent = title || '';
      bar.setAttribute('aria-label', lbl('now', 'Style preview') + (title ? (': ' + title) : ''));
    }

    function updatePlayBtn() {
      playBtn.innerHTML = playing ? ICON_PAUSE : ICON_PLAY;
      playBtn.setAttribute('aria-label', playing ? lbl('pause', 'Pause') : lbl('play', 'Play'));
    }

    function emitState() {
      if (onStateCb) { try { onStateCb(playing ? activeKey : null, playing); } catch (e) {} }
    }
    // Clear every card indicator at once (used the instant a switch begins, so
    // the previous card's ▶ doesn't linger as "playing" while the next loads).
    function emitClear() {
      if (onStateCb) { try { onStateCb(null, false); } catch (e) {} }
    }

    /* ── reveal / collapse (one-time smooth reveal; static in-flow, no sticky) ── */
    function reveal() {
      if (revealed) { return; }
      revealed = true;
      host.style.maxHeight = host.scrollHeight + 'px';
      host.style.opacity = '1';
      // After the reveal animation settles, drop the clip so the volume popup
      // (which sits ABOVE the bar) is not cut off by .spp-host{overflow:hidden}.
      window.setTimeout(function () { if (revealed) { host.style.maxHeight = 'none'; host.style.overflow = 'visible'; } }, REVEAL_MS + 40);
      window.setTimeout(function () { draw(); }, 40);
      if (!firstRevealFired) { firstRevealFired = true; if (onFirstReveal) { try { onFirstReveal(); } catch (e) {} } }
    }
    function collapse() {
      if (!revealed) { return; }
      closeVol();
      host.style.overflow = 'hidden';   // re-clip for the max-height animation
      var h = host.scrollHeight;
      host.style.maxHeight = h + 'px';
      // force reflow so the transition runs from an explicit px height
      if (host.offsetHeight) { /* read triggers reflow */ }
      host.style.maxHeight = '0';
      host.style.opacity = '0';
      revealed = false;
    }

    /* ── playback control ── */
    function pauseAudio() {
      try { audio.pause(); } catch (e) {}
    }
    // A play() promise rejection is USUALLY benign — an AbortError from the
    // src change / pause interrupting an in-flight play, or a NotAllowedError.
    // It does NOT mean the file is missing. The ONLY reliable "sample broken/
    // 404" signal is the audio 'error' event (a real MediaError), which is the
    // sole trigger of the «пример скоро» path. So swallow play() rejections:
    // if the file loads, 'playing' commits it; if it's truly broken, 'error'
    // handles it. (Previously this showed the toast AND then played anyway.)
    function safePlay() {
      // Образец — не единственный звук на странице: рядом играет результат
      // генерации. Общий владелец звука решает, кто звучит (#6901).
      try { if (window.__soloAudio) window.__soloAudio.claim(audio); } catch (e) {}
      var p = audio.play();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); }
    }
    function resumeAudio() {
      safePlay();
    }

    function switchTo(key, title) {
      var isSwitch = !!activeKey && activeKey !== key;
      var url = sampleBase + '/' + encodeURIComponent(key) + '.mp3' + (sampleVer ? '?v=' + encodeURIComponent(sampleVer) : '');
      pending = { key: key, title: title, url: url };
      // Drop the previous card's playing indicator immediately (the next track
      // is not playing yet — nothing should read as "playing" mid-switch).
      emitClear();
      if (aeFn) {
        if (isSwitch) { aeFn('sample_switch', { from: activeKey, to: key }); }
        aeFn('sample_play', { style: key });
      }
      // start the peak decode in parallel — playback never waits for it
      ensurePeaks(url);
      pauseAudio();
      try { audio.src = url; } catch (e) {}
      try { audio.currentTime = 0; } catch (e) {}
      duration = 0; curTime = 0;
      updateTime();
      if (revealed) { setTitle(title); draw(); }
      safePlay();
    }

    function handleFailure() {
      if (!pending) { return; }
      var pd = pending;
      pending = null;
      if (onMissingCb) { try { onMissingCb(pd.key); } catch (e) {} }
      if (committed) {
        // keep the previously-loaded track visible (it was stopped by the switch)
        activeKey = committed.key; activeUrl = committed.url;
        setTitle(committed.title);
        peaksReady = !!peaksCache[activeUrl];
        playing = false;
        updatePlayBtn();
        draw();
        emitState();
      } else {
        // nothing was ever loaded — do NOT reveal; collapse if somehow open
        activeKey = null; activeUrl = null;
        playing = false;
        updatePlayBtn();
        emitState();
        if (revealed) { collapse(); }
      }
    }

    /* ── audio element events (attached once) ── */
    audio.addEventListener('loadedmetadata', function () {
      if (isFinite(audio.duration) && audio.duration > 0) { duration = audio.duration; updateTime(); draw(); }
    });
    audio.addEventListener('durationchange', function () {
      if (isFinite(audio.duration) && audio.duration > 0) { duration = audio.duration; updateTime(); }
    });
    audio.addEventListener('playing', function () {
      if (pending) {
        committed = { key: pending.key, title: pending.title, url: pending.url };
        activeKey = pending.key; activeUrl = pending.url;
        setTitle(committed.title);
        peaksReady = !!peaksCache[activeUrl];
        ensurePeaks(activeUrl);
        pending = null;
        reveal();
        draw();
      }
      playing = true;
      updatePlayBtn();
      emitState();
    });
    audio.addEventListener('timeupdate', function () {
      if (dragging) { return; }
      curTime = audio.currentTime;
      updateTime();
      draw();
    });
    audio.addEventListener('pause', function () {
      // ignore the pause fired while a switch tears down the old src
      if (pending) { return; }
      if (!playing) { return; }
      playing = false;
      updatePlayBtn();
      emitState();
    });
    audio.addEventListener('ended', function () {
      // STAY visible, reset to 0, paused — deliberately no hide (page must not jump)
      playing = false;
      try { audio.currentTime = 0; } catch (e) {}
      curTime = 0;
      updateTime();
      draw();
      updatePlayBtn();
      emitState();
    });
    audio.addEventListener('error', function () {
      if (pending) { handleFailure(); }
    });

    /* ── seek: click + drag on the waveform (RTL mirrored) ── */
    function seekFromClientX(clientX) {
      if (!(duration > 0) || !isFinite(duration)) { return; }
      var rect = canvas.getBoundingClientRect();
      var w = rect.width || 1;
      var r = (clientX - rect.left) / w;
      if (rtl) { r = 1 - r; }
      if (r < 0) { r = 0; }
      if (r > 1) { r = 1; }
      try { audio.currentTime = r * duration; } catch (e) {}
      curTime = r * duration;
      updateTime();
      draw();
      var now = Date.now();
      if (aeFn && (now - lastSeekAe) > SEEK_AE_MS) {
        lastSeekAe = now;
        aeFn('sample_seek', { style: activeKey || '', pct: Math.round(r * 100) });
      }
    }
    canvas.addEventListener('pointerdown', function (e) {
      if (!committed && !pending) { return; }
      dragging = true;
      try { canvas.setPointerCapture(e.pointerId); } catch (er) {}
      seekFromClientX(e.clientX);
      e.preventDefault();
    });
    canvas.addEventListener('pointermove', function (e) {
      if (dragging) { seekFromClientX(e.clientX); }
    });
    function endDrag(e) {
      if (!dragging) { return; }
      dragging = false;
      if (e && e.pointerId != null) { try { canvas.releasePointerCapture(e.pointerId); } catch (er) {} }
    }
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);
    // keyboard seek (a11y) — time-based ±5s
    canvas.addEventListener('keydown', function (e) {
      if (!(duration > 0)) { return; }
      var t = curTime, handled = true;
      if (e.key === 'ArrowRight') { t += 5; }
      else if (e.key === 'ArrowLeft') { t -= 5; }
      else if (e.key === 'Home') { t = 0; }
      else if (e.key === 'End') { t = duration; }
      else { handled = false; }
      if (!handled) { return; }
      e.preventDefault();
      if (t < 0) { t = 0; }
      if (t > duration) { t = duration; }
      try { audio.currentTime = t; } catch (er) {}
      curTime = t; updateTime(); draw();
    });

    playBtn.addEventListener('click', function () {
      if (activeKey && committed) {
        if (playing) { pauseAudio(); } else { resumeAudio(); }
      }
    });

    /* keep the canvas crisp on width changes (no scroll follow — static bar) */
    if (window.ResizeObserver) {
      try { new ResizeObserver(function () { draw(); }).observe(canvas); } catch (e) {}
    } else {
      window.addEventListener('resize', function () { draw(); });
    }

    /* ── controller ── */
    function ctrlToggle(key, title) {
      if (!key) { return; }
      if (activeKey === key && committed) {
        if (playing) { pauseAudio(); } else { resumeAudio(); }
        return;
      }
      switchTo(key, title || key);
    }
    function ctrlPlay(key, title) {
      if (!key) { return; }
      if (activeKey === key && committed) {
        if (!playing) { resumeAudio(); }
        return;
      }
      switchTo(key, title || key);
    }
    function ctrlHide() {
      pauseAudio();
      playing = false;
      updatePlayBtn();
      collapse();
      emitState();
    }
    function ctrlIsPlaying(key) {
      if (key == null) { return playing; }
      return playing && activeKey === key;
    }
    function ctrlDestroy() {
      try { pauseAudio(); } catch (e) {}
      stopPlaceholderAnim();
      try { if (host.parentNode) { host.parentNode.removeChild(host); } } catch (e) {}
    }

    return {
      play: ctrlPlay,
      toggle: ctrlToggle,
      hide: ctrlHide,
      isPlaying: ctrlIsPlaying,
      destroy: ctrlDestroy
    };
  }

  function stubController() {
    return {
      play: function () {},
      toggle: function () {},
      hide: function () {},
      isPlaying: function () { return false; },
      destroy: function () {}
    };
  }

  window.StylePreviewPlayer = { mount: mount, __real: true, version: '1.0.0' };
})();
